import { Method } from '_lib/api';
import apiRoute from '_lib/api/paths';
import { getFetchHeaders } from '_lib/api/utils';
import React, { FC, useEffect, useState } from 'react';

import { APP_VERSION } from '../../../../appVersion';
import { Client, getAppEnvironmentLabel, isAppProduction, mapClient } from '../../../../utils';
import logoEmeras from '../../../assets/images/logo-emeras.png';
import logoEmerasSinelec from '../../../assets/images/logo-emeras-sinelec.png';
import styles from './styles';

type BackendVersions = {
  ingester: string | null;
  distributor: string | null;
  core: string | null;
};

const EMPTY_BACKEND_VERSIONS: BackendVersions = {
  ingester: null,
  distributor: null,
  core: null,
};

const formatPill = (name: string, version: string | null, withVPrefix = false): string => {
  if (version == null || version === '') {
    return `${name} -`;
  }
  return withVPrefix ? `${name} v${version}` : `${name} ${version}`;
};

const TopMenu: FC = () => {
  const classes = styles();
  const clientName = process.env.CLIENT_NAME;
  const client = mapClient(clientName || '');
  const environmentLabel = getAppEnvironmentLabel();
  const isProduction = isAppProduction();
  const [backendVersions, setBackendVersions] = useState<BackendVersions>(EMPTY_BACKEND_VERSIONS);

  useEffect(() => {
    const hostname = process.env.API_ENDPOINT;
    if (!hostname) {
      return undefined;
    }

    let cancelled = false;
    const headers = getFetchHeaders({ authenticated: true, method: Method.GET });

    fetch(`${hostname}/${apiRoute.getVersion}`, { headers })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`version request failed: ${response.status}`);
        }
        return response.json();
      })
      .then((data: Partial<BackendVersions>) => {
        if (cancelled) return;
        setBackendVersions({
          ingester: data?.ingester ?? null,
          distributor: data?.distributor ?? null,
          core: data?.core ?? null,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setBackendVersions(EMPTY_BACKEND_VERSIONS);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const renderClientLogo = (clientConfig: Client) => (
    <img src={clientConfig.logoPath} alt={clientConfig.name} className={classes.logoImage} />
  );

  const versionPills: Array<[string, string]> = [
    ['console', formatPill('console', APP_VERSION, true)],
    ['ingester', formatPill('ingester', backendVersions.ingester, true)],
    ['distributor', formatPill('distributor', backendVersions.distributor, true)],
    ['core', formatPill('core', backendVersions.core, true)],
  ];

  return (
    <div className={classes.drawerPaper}>
      <img
        src={client.name === '5t' ? logoEmerasSinelec : logoEmeras}
        alt="logo-emeras-sinelec"
        className={classes.logoImage}
      />
      <div className={classes.environmentBanner}>
        {isProduction && <span className={classes.productionDot} aria-hidden />}
        <span className={classes.environmentLabel}>{environmentLabel}</span>
        <div className={classes.versionPills} aria-label="component versions">
          {versionPills.map(([id, label]) => (
            <span key={id} className={classes.versionPill}>
              {label}
            </span>
          ))}
        </div>
      </div>
      {client.url ? (
        <a href={client.url} target="_blank" rel="noreferrer">
          {renderClientLogo(client)}
        </a>
      ) : (
        renderClientLogo(client)
      )}
    </div>
  );
};

export default React.memo(TopMenu);
